import { AutoComplete } from "primereact/autocomplete";
import { classNames as primeClassess } from "primereact/utils";
import React, { useEffect, useState } from "react";
import {
  Control,
  Controller,
  useForm,
  UseFormClearErrors,
  UseFormSetError,
} from "react-hook-form";
import classNames from "classnames/bind";
import classes from "./Select.module.scss";
import { useTranslation } from "react-i18next";

type SelectProps = {
  name: string;
  label?: string;
  display?: string;
  control?: Control<any>;
  options: any;
  required?: boolean;
  errorMessage?: string;
  placeholder?: string;
  value?: any;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  onChange?: (value?: any) => void;
  /*
    use these if using Select outside of form and useForm
    otherwise use form control  
  */
  setError?: UseFormSetError<any>;
  clearErrors?: UseFormClearErrors<any>;
  /* --------- */
};

const Select = ({
  name,
  label,
  options,
  display = "label",
  control,
  required = true,
  errorMessage,
  placeholder,
  onChange,
  clearErrors,
  value,
  disabled = false,
  readOnly = false,
  className,
  setError,
}: SelectProps): React.ReactElement => {
  const { t } = useTranslation();
  const cx = classNames.bind(classes);
  const { control: mockControl } = useForm<any>();

  const [suggestions, setSuggestions] = useState(options);
  const [selected, setSelected] = useState(value);

  const search = (event: { query: string }) => {
    const newSuggestions = options?.filter((option: any) =>
      option[display]?.toLowerCase().includes(event.query.toLowerCase())
    );

    return setSuggestions(newSuggestions);
  };

  useEffect(() => {
    if (selected === "" && required && setError) {
      return setError(name, {
        message: `${label} ${t("required_message_is_required")}`,
      });
    }

    if (clearErrors) clearErrors(name);
    // eslint-disable-next-line
  }, [selected]);

  return (
    <div
      className={cx(classes.selectContainer, { disabled: disabled }, className)}
    >
      <label
        htmlFor={name}
        className={primeClassess({
          "p-error": errorMessage,
        })}
      >
        <span className={cx(classes.labelText)}>{label}</span>
        <span>{required && !readOnly ? "*" : ""}</span>
      </label>
      <Controller
        name={name}
        control={control || mockControl}
        rules={{
          required: required
            ? `${label} ${t("required_message_is_required")}.`
            : undefined,
        }}
        render={({ field, fieldState }) => (
          <AutoComplete
            disabled={disabled || readOnly}
            id={field.name}
            name={field.name}
            className={primeClassess({
              "p-invalid": fieldState.error || errorMessage,
              [classes.readOnly]: readOnly,
            })}
            readOnly={readOnly}
            placeholder={placeholder || t("interface_placeholder_select")}
            field={display}
            value={selected !== undefined ? selected : field.value}
            suggestions={suggestions}
            completeMethod={search}
            onChange={(e) => {
              setSelected(e.target.value);
              if (!e.target.value) field.onChange(e.value);
            }}
            onSelect={(e) => {
              if (e.value !== value) {
                onChange ? onChange(e.value) : field.onChange(e.value);
              }
            }}
            dropdown
            forceSelection
            aria-label={label}
          />
        )}
      />

      <div className={cx(classes.errorMessage)}>
        {errorMessage ? (
          <small className="p-error">{errorMessage}</small>
        ) : null}
      </div>
    </div>
  );
};

export default Select;
